import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Stack,
    Link,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ApplepayIcon, PaypalIcon, GiftIcon} from '../../../components/icons'
import {getCreditCardIcon, buildMaskedCardDisplay} from '../../../utils/cc-utils'
import {useBrand} from '../../../contexts/brand-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// CONSTANTS
import {CREDIT_CARD, PAYPAL, APPLE_PAY, GIFT_CARD, CASH, COUNTRY, PAYMENT_METHODS} from '../constant'
import {useMultiStyleConfig, useBreakpointValue} from '@chakra-ui/react'

const BillingAddress = ({street, city, postalCode, state, billingFirstName, billingLastName, billingPhoneNo}) => {
    const styles = useMultiStyleConfig('OrderDetail')

    const getStreetAndApartment = (street) => {
        if (!street) return {}

        const match = street.match(/^(.*?)(Apartment|Apt|Flat|Suite|Unit)\s*(.*)$/i)

        if (match) {
            return {
                streetName: match[1].trim(),
                apartment: `${match[2]} ${match[3]}`.trim()
            }
        }

        return {streetName: street}
    }

    const {streetName, apartment} = getStreetAndApartment(street)

    return (
        <Stack alignItems="flex-start" spacing={1}>
            <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                <FormattedMessage
                    id="account_order_detail.label.billing_address"
                    defaultMessage="Billing Address"
                />
            </Text>
            <Text {...styles.billingNameText}>
                {`${billingFirstName || ''} ${billingLastName || ''}`}
            </Text>


            {(streetName || apartment || city || state || postalCode) && (
                <Text {...styles.billingStreetText}>
                    {[streetName, apartment, city, state, postalCode].filter(Boolean).join(', ')}
                </Text>
            )}

            <Text {...styles.billingPhoneNoText}>{billingPhoneNo}</Text>
        </Stack>
    )
}

BillingAddress.propTypes = {
    street: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    billingFirstName: PropTypes.string,
    billingLastName: PropTypes.string,
    billingPhoneNo: PropTypes.string
}

const CreditCardPayment = ({payment}) => {
    const CardIcon = getCreditCardIcon(payment?.name)
    const styles = useMultiStyleConfig('OrderDetail')

    return (
        <Stack alignItems="flex-start" spacing={1}>
            <Text {...styles.paymentCardNameText}>
                {payment?.name || (
                    <FormattedMessage
                        id="account_order_detail.label.credit_card"
                        defaultMessage="Credit Card"
                    />
                )}
            </Text>

            <Flex alignItems="center" gap={6} fontSize="sm">
                <Flex gap={3} alignItems="center">
                    <CardIcon w="42px" h="100%" />
                    <Text {...styles.paymentCardNumberText} fontSize="sm" fontWeight="bold">
                        {buildMaskedCardDisplay(payment?.cardNumber?.slice(-4), 4)}
                    </Text>
                </Flex>

                {payment?.expirationMonth && payment?.expirationYear && (
                    <Text {...styles.paymentCardDateText}>
                        <FormattedMessage
                            id="account_order_detail.label.expires"
                            defaultMessage="{month}/{year}"
                            values={{
                                month: String(payment.expirationMonth).padStart(2, '0'),
                                year: payment.expirationYear
                            }}
                        />
                    </Text>
                )}
            </Flex>
        </Stack>
    )
}

CreditCardPayment.propTypes = {
    payment: PropTypes.shape({
        name: PropTypes.string,
        cardNumber: PropTypes.string,
        expirationMonth: PropTypes.string,
        expirationYear: PropTypes.string
    })
}

const PayPalPayment = () => {
    const styles = useMultiStyleConfig('OrderDetail')
    return (
        <Stack alignItems="flex-start" spacing={2} mb={4}>
            <Text {...styles.paymentCardNameText}>
                <FormattedMessage id="account_order_detail.label.paypal" defaultMessage="PayPal" />
            </Text>

            <Flex gap={3} fontSize="sm">
                <PaypalIcon w="42px" />
                <Text>
                    <FormattedMessage
                        id="account_order_detail.paypal.subtext"
                        defaultMessage="You can view your complete PayPal transaction"
                    />{' '}
                    <Link href="https://paypal.com" textDecoration="underline">
                        <FormattedMessage
                            id="account_order_detail.paypal.link"
                            defaultMessage="here"
                        />
                    </Link>
                </Text>
            </Flex>
        </Stack>
    )
}

const ApplePayPayment = () => {
    const styles = useMultiStyleConfig('OrderDetail')
    return (
        <Stack alignItems="flex-start" spacing={2} mb={4}>
            <Text {...styles.paymentCardNameText}>
                <FormattedMessage
                    id="account_order_detail.label.apple_pay"
                    defaultMessage="Apple Pay"
                />
            </Text>

            <Flex gap={3} fontSize="sm">
                <ApplepayIcon w="42px" />
                <Text>
                    <FormattedMessage
                        id="account_order_detail.applepay.subtext"
                        defaultMessage="Payment processed through Apple Pay"
                    />
                </Text>
            </Flex>
        </Stack>
    )
}

const GiftCardPayment = ({payment}) => {
    const intl = useIntl()
    const {site, locale} = useMultiSite()
    const {l10n} = site
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    const formattedAmount = intl.formatNumber(payment?.amount || 0, {
        style: 'currency',
        currency: currency
    })

    const styles = useMultiStyleConfig('OrderDetail')

    return (
        <Stack alignItems="flex-start" spacing={1} mt={2}>
            <Text {...styles.paymentCardNameText}>
                <FormattedMessage
                    id="account_order_detail.label.gift_card"
                    defaultMessage="Gift Card"
                />
            </Text>

            <Flex {...styles.giftCardContainer}>
                <Text {...styles.paymentCardNumberText}>
                    {buildMaskedCardDisplay(payment?.cardNumber?.slice(-4), 4)}
                </Text>

                <Text {...styles.giftCardAmount}>{formattedAmount}</Text>
            </Flex>
        </Stack>
    )
}

GiftCardPayment.propTypes = {
    payment: PropTypes.shape({
        cardNumber: PropTypes.string,
        amount: PropTypes.number
    })
}

const POSPayment = ({payment}) => {
    const intl = useIntl()
    const {site, locale} = useMultiSite()
    const {l10n} = site
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    const formattedAmount = intl.formatNumber(payment?.amount || 0, {
        style: 'currency',
        currency: currency
    })

    return (
        <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Text fontWeight="bold" textTransform="uppercase" fontSize="sm">
                {payment?.name || (
                    <FormattedMessage
                        id="account_order_detail.label.pos_payment"
                        defaultMessage="Cash"
                    />
                )}
            </Text>

            <Text fontSize="sm" fontWeight="bold">
                {formattedAmount}
            </Text>
        </Flex>
    )
}

POSPayment.propTypes = {
    payment: PropTypes.shape({
        name: PropTypes.string,
        amount: PropTypes.number
    })
}

const PaymentMethod = ({paymentMethod}) => {
    const styles = useMultiStyleConfig('OrderDetail')
    if (!paymentMethod?.length) return null

    return (
        <>
            {paymentMethod.map((payment, index) => {
                const type = payment?.paymentMethod?.toLowerCase()

                switch (type) {
                    case CREDIT_CARD:
                        return <CreditCardPayment key={index} payment={payment} />

                    case PAYPAL:
                        return <PayPalPayment key={index} />

                    case APPLE_PAY:
                        return <ApplePayPayment key={index} />

                    case CASH:
                        return <POSPayment key={index} payment={payment} />

                    default:
                        return (
                            <Text key={index} {...styles.paymentCardNameText} mb={4}>
                                {payment?.name}
                            </Text>
                        )
                }
            })}
        </>
    )
}

PaymentMethod.propTypes = {
    paymentMethod: PropTypes.arrayOf(
        PropTypes.shape({
            paymentMethod: PropTypes.string,
            name: PropTypes.string,
            cardNumber: PropTypes.string,
            expirationMonth: PropTypes.string,
            expirationYear: PropTypes.string
        })
    ).isRequired
}

const InStoreOrderPayment = ({nonGiftCardPayments, giftCardPayments}) => {
    const hasPayments =
        (nonGiftCardPayments && nonGiftCardPayments.length > 0) ||
        (giftCardPayments && giftCardPayments.length > 0)

    if (!hasPayments) return null

    const showDivider = nonGiftCardPayments?.length > 0 && giftCardPayments?.length > 0

    return (
        <Box
            display="flex"
            flexDirection="column"
            border="1px solid"
            borderColor="gray.200"
            gap="16px"
            padding="20px"
        >
            <Text
                fontSize="sm"
                fontWeight="bold"
                textTransform="uppercase"
                mb={4}
                lineHeight="20px"
            >
                <FormattedMessage
                    defaultMessage="Payment Method"
                    id="account_order_detail.label.payment.method"
                />
            </Text>

            <PaymentMethod paymentMethod={nonGiftCardPayments} />
            {showDivider && (
                <Divider display={{base: 'block', lg: 'block'}} borderColor="grey.500" />
            )}

            {(giftCardPayments || []).map((payment, index) => (
                <GiftCardPayment key={payment?.cardNumber || index} payment={payment} />
            ))}
        </Box>
    )
}

InStoreOrderPayment.propTypes = {
    nonGiftCardPayments: PropTypes.arrayOf(
        PropTypes.shape({
            paymentMethod: PropTypes.string,
            name: PropTypes.string,
            amount: PropTypes.number,
            cardNumber: PropTypes.string
        })
    ),

    giftCardPayments: PropTypes.arrayOf(
        PropTypes.shape({
            paymentMethod: PropTypes.string,
            name: PropTypes.string,
            cardNumber: PropTypes.string,
            expirationMonth: PropTypes.string,
            expirationYear: PropTypes.string
        })
    )
}

export const OrderPayment = ({order}) => {
    const payments = order?.payment || []
    const isInStoreOrder = order?.isOnlineOrder === false

    const {giftCardPayments, nonGiftCardPayments} = payments.reduce(
        (acc, payment) => {
            const isGiftCard = payment?.paymentMethod?.toLowerCase() === PAYMENT_METHODS.GIFT_CARD

            if (isGiftCard) {
                acc.giftCardPayments.push(payment)
            } else {
                acc.nonGiftCardPayments.push(payment)
            }

            return acc
        },
        {giftCardPayments: [], nonGiftCardPayments: []}
    )

    const styles = useMultiStyleConfig('OrderDetail')

    return isInStoreOrder ? (
        <InStoreOrderPayment
            nonGiftCardPayments={nonGiftCardPayments}
            giftCardPayments={giftCardPayments}
            order={order}
        />
    ) : (
        <Box {...styles.paymentMethodContainer}>
            <Text {...styles.paymentMethodText}>
                <FormattedMessage
                    defaultMessage="Payment Method"
                    id="account_order_detail.label.payment.method"
                />
            </Text>
            <PaymentMethod paymentMethod={nonGiftCardPayments} />
            <Divider display={{base: 'block', lg: 'block'}} borderColor="grey.500" />
            <BillingAddress {...order.billingAddress} />
            {(giftCardPayments || []).map((payment, index) => (
                <>
                    <Divider display={{base: 'block', lg: 'block'}} borderColor="grey.600" />
                    <GiftCardPayment key={payment?.cardNumber || index} payment={payment} />
                </>
            ))}
        </Box>
    )
}

OrderPayment.propTypes = {
    order: PropTypes.shape({
        payment: PropTypes.array.isRequired,
        billingAddress: PropTypes.shape({
            street: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            postalCode: PropTypes.string,
            country: PropTypes.string,
            billingFirstName: PropTypes.string,
            billingLastName: PropTypes.string,
            billingPhoneNo: PropTypes.string
        })
    }).isRequired
}
